import { Colors } from "../../config/DesignConstants";
import { mod, polarToVec } from "../../core/util/MathUtil";
import { createRegularPolygon } from "../../core/util/PolygonUtils";
import { rInteger } from "../../core/util/Random";
import { V, V2d } from "../../core/Vector";
import { BigBomb } from "../munitions/BigBomb";
import { BaseEnemy } from "./BaseEnemy";

//=============================================================================
// BOMBER CONFIGURATION - All tweakable values in one place
//=============================================================================

const BOMBER_CONFIG = {
  // Basic stats
  health: 100,
  killPoints: 50, // Higher score for bombers
  mass: 3, // Heavy and slow

  // Visual
  color: Colors.CORAL,
  secondaryColor: Colors.DARK_CORAL,
  strokeWidth: 2,
  size: 12,
  shape: createRegularPolygon(6, 12), // Hexagon

  // Orbital movement
  orbitRadius: 200, // Distance from center for waypoints
  numWaypoints: 6, // Number of orbital waypoints
  reachDistance: 40, // Distance to consider waypoint "reached"

  // Movement speeds
  maxSpeed: 80, // Maximum orbital speed
  acceleration: 40, // Acceleration toward waypoints
  deceleration: 30, // Deceleration when approaching waypoints
  minStopSpeed: 5, // Speed below which we snap to full stop

  // Bombing behavior
  pauseTime: 1.5, // Seconds to pause at each waypoint
  bombBurstSize: 1, // Number of bombs per attack
  bombSpreadRadius: 0, // Spread radius for bomb pattern
  bombingDelay: 0.5, // Delay into pause before bombing (as fraction of pauseTime)
} as const;

//=============================================================================
// BOMBER ENEMY CLASS - Orbital bomber with clear, readable behavior
//=============================================================================

export class BomberEnemy extends BaseEnemy {
  // Orbital waypoint state
  private waypoints: V2d[] = [];
  private currentWaypointIndex = 0;
  private isPaused = false;
  private pauseTimer = 0;
  private hasDroppedBombs = false;

  constructor(position: V2d) {
    super(position, {
      health: BOMBER_CONFIG.health,
      killPoints: BOMBER_CONFIG.killPoints,
      mass: BOMBER_CONFIG.mass,
      visualConfig: {
        points: [...BOMBER_CONFIG.shape],
        color: BOMBER_CONFIG.color,
        secondaryColor: BOMBER_CONFIG.secondaryColor,
        strokeWidth: BOMBER_CONFIG.strokeWidth,
      },
    });

    this.generateOrbitalWaypoints();
  }

  //===========================================================================
  // MAIN BEHAVIOR - Clear orbital movement with bombing
  //===========================================================================

  onTick(dt: number): void {
    if (!this.body || this.waypoints.length === 0) return;

    this.rotateToFaceDownward();

    if (this.isPaused) {
      this.handlePauseAtWaypoint(dt);
    } else {
      this.moveTowardCurrentWaypoint();
      this.checkWaypointArrival();
    }
  }

  //===========================================================================
  // MOVEMENT LOGIC - Broken into clear, understandable pieces
  //===========================================================================

  private generateOrbitalWaypoints(): void {
    // Generate waypoints in a higher orbit pattern
    for (let i = 0; i < BOMBER_CONFIG.numWaypoints; i++) {
      const angle = (i / BOMBER_CONFIG.numWaypoints) * Math.PI * 2;
      const waypoint = polarToVec(angle, BOMBER_CONFIG.orbitRadius);
      this.waypoints.push(waypoint);
    }

    // Start at a random waypoint index for varied orbital direction
    this.currentWaypointIndex = rInteger(0, this.waypoints.length);
  }

  private rotateToFaceDownward(): void {
    // Use base class method to face downward
    this.aimDown();
  }

  private moveTowardCurrentWaypoint(): void {
    const currentPos = V(this.body!.position);
    const targetWaypoint = this.waypoints[this.currentWaypointIndex];
    const currentVelocity = V(this.body!.velocity);
    const currentSpeed = currentVelocity.magnitude;

    // Calculate desired movement
    const toWaypoint = targetWaypoint.sub(currentPos);
    const desiredDirection = toWaypoint.normalize();
    const desiredVelocity = desiredDirection.mul(BOMBER_CONFIG.maxSpeed);

    // Calculate steering force
    const steeringForce = desiredVelocity.sub(currentVelocity);

    // Apply acceleration (reduced when near max speed for smoother movement)
    const forceMultiplier =
      currentSpeed < BOMBER_CONFIG.maxSpeed * 0.9
        ? BOMBER_CONFIG.acceleration
        : BOMBER_CONFIG.acceleration * 0.3;

    const limitedForce = steeringForce
      .normalize()
      .mul(forceMultiplier * this.body!.mass);

    this.body!.applyForce([limitedForce.x, limitedForce.y]);
  }

  private checkWaypointArrival(): void {
    const currentPos = V(this.body!.position);
    const targetWaypoint = this.waypoints[this.currentWaypointIndex];
    const distanceToWaypoint = targetWaypoint.sub(currentPos).magnitude;

    if (distanceToWaypoint < BOMBER_CONFIG.reachDistance) {
      this.startPauseAtWaypoint();
    }
  }

  private startPauseAtWaypoint(): void {
    this.isPaused = true;
    this.pauseTimer = BOMBER_CONFIG.pauseTime;
    this.hasDroppedBombs = false;
  }

  private handlePauseAtWaypoint(dt: number): void {
    this.pauseTimer -= dt;

    // Decelerate to a stop
    this.decelerateToStop();

    // Drop bombs partway through the pause
    const bombingTime = BOMBER_CONFIG.pauseTime * BOMBER_CONFIG.bombingDelay;
    if (
      !this.hasDroppedBombs &&
      this.pauseTimer <= BOMBER_CONFIG.pauseTime - bombingTime
    ) {
      this.dropBombs();
      this.hasDroppedBombs = true;
    }

    // Move to next waypoint after pause
    if (this.pauseTimer <= 0) {
      this.moveToNextWaypoint();
    }
  }

  private decelerateToStop(): void {
    const currentVelocity = V(this.body!.velocity);
    const currentSpeed = currentVelocity.magnitude;

    if (currentSpeed > 0.1) {
      const decelerationForce = currentVelocity
        .normalize()
        .mul(-BOMBER_CONFIG.deceleration * this.body!.mass);
      this.body!.applyForce([decelerationForce.x, decelerationForce.y]);

      // Snap to full stop when very slow
      if (currentSpeed < BOMBER_CONFIG.minStopSpeed) {
        this.body!.velocity = [0, 0];
      }
    } else {
      this.body!.velocity = [0, 0];
    }
  }

  private moveToNextWaypoint(): void {
    this.currentWaypointIndex = mod(
      this.currentWaypointIndex + 1,
      this.waypoints.length,
    );
    this.isPaused = false;
  }

  //===========================================================================
  // BOMBING - Simple and clear
  //===========================================================================

  private dropBombs(): void {
    if (!this.game) return;

    const currentLevel = this.getCurrentLevel();
    if (!currentLevel) return;

    for (let i = 0; i < BOMBER_CONFIG.bombBurstSize; i++) {
      // Create bombs in a spread pattern (currently just single bomb)
      const angle = (i / BOMBER_CONFIG.bombBurstSize) * Math.PI * 2;
      const offset = polarToVec(angle, BOMBER_CONFIG.bombSpreadRadius);

      const bombPos = V(this.body!.position).add(offset);
      const bomb = new BigBomb(bombPos);

      currentLevel.addChild(bomb);
    }
  }
}
